import {
  createOrderByComment,
  createOrderByMessage,
} from '@/api/order-api';
import { useToast } from '@/hooks/use-toast';
import {
  ChatOrderRequest,
  CommentOrderRequest,
} from '@/types/order-type';
import { PinnedVariantWithStock } from '@/types/sale-type';
import { VariantQuantityRows } from './variant-quantity-rows';

export type PinnedVariantBoardProps = {
  variants: PinnedVariantWithStock[];
  activeId: string;
  onRefresh?: (variantId: string) => void;
};

export function PinnedVariantBoard({
  variants,
  activeId,
  onRefresh,
}: PinnedVariantBoardProps) {
  const activeVariant = variants.find((item) => item.id === activeId);
  const { toast } = useToast();

  const handleAssignSlot = async (args: {
    payload: any;
    variant: PinnedVariantWithStock;
  }): Promise<boolean> => {
    const { payload, variant } = args;
    if (
      payload.type == 'facebook_comment' ||
      payload.type == 'queue_customer' ||
      payload.lastActionType == 'COMMENT'
    ) {
      let requestData: CommentOrderRequest | null = null;
      if (payload.type == 'facebook_comment') {
        requestData = {
          fromName: payload.fromName,
          fromId: payload.fromId,
          id: payload.id,
          message: payload.message,
          variantId: variant.id,
        };
      } else {
        requestData = {
          fromName: payload.name,
          id: payload.id,
          message: payload.lastComment,
          variantId: variant.id,
          fromId: payload.facebookId,
        };
      }
      const resp = await createOrderByComment(requestData);
      if (resp.success) {
        onRefresh?.(variant.id);
        toast({
          title: 'Success',
          description: `Creating order for customer ${requestData.fromName} by comment successfully`,
          variant: 'success',
        });
        return true;
      } else {
        const respMessage =
          resp.message || 'Unexpected error in creating order by comment';
        toast({
          title: 'Error',
          description: respMessage,
          variant: 'destructive',
        });
        return false;
      }
    } else if (
      payload.type == 'conversation' ||
      payload.type == 'queue_customer' ||
      payload.lastActionType == 'MESSAGE'
    ) {
      let requestData: ChatOrderRequest | null = null;
      if (payload.type == 'conversation') {
        requestData = {
          id: payload.id,
          conversationId: payload.conversationId,
          snippet: payload.lastMessage,
          variantId: variant.id,
          user: {
            id: payload.psid,
            name: payload.name,
          },
        };
      } else {
        requestData = {
          id: payload.queueId,
          conversationId: payload.conversationId,
          snippet: payload.lastMessage,
          variantId: variant.id,
          user: {
            id: payload.facebookId,
            name: payload.name,
          },
        };
      }
      const resp = await createOrderByMessage(requestData);
      if (resp.success) {
        toast({
          title: 'Success',
          description: `Creating order for customer ${requestData.user.name} by chat successfully`,
          variant: 'success',
        });
        onRefresh?.(variant.id);
        return true;
      } else {
        const respMessage =
          resp.message || 'Unexpected error in creating order by chat';
        toast({
          title: 'Error',
          description: respMessage,
          variant: 'destructive',
        });
        return false;
      }
    }
    return false;
  };

  if (!activeVariant) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center border-t border-slate-100 bg-slate-50/50">
        <div className="text-sm text-slate-500">
          Select a pinned variant to view its quantity chips.
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden border-t border-slate-100 bg-slate-50/50">
      <VariantQuantityRows
        variant={activeVariant}
        onAssignToSlot={handleAssignSlot}
        onRefresh={onRefresh}
      />
    </div>
  );
}
